import { useState, useEffect } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';

import { Alert, AlertDescription } from '@/components/ui/alert';

import { Sparkles, Plus, AlertTriangle, CheckCircle } from 'lucide-react';
import { OptimizedEditableCriterionCard } from './OptimizedEditableCriterionCard';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface OptimizedCriteriaSectionProps {
  control: Control<any>;
  aiGeneratedCriteria?: any[];
  campaignData?: {
    candidate_cv_url?: string;
    job_description_file_url?: string;
    job_description_link?: string;
  };
  onSaveDraft?: () => void; // Fonction de sauvegarde automatique
}

export const OptimizedCriteriaSection = ({ 
  control, 
  aiGeneratedCriteria, 
  campaignData,
  onSaveDraft
}: OptimizedCriteriaSectionProps) => {
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'selected_evaluation_criteria',
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const selectedCriteria = useWatch({
    control,
    name: 'selected_evaluation_criteria',
  }) || [];

  const criteriaCount = selectedCriteria.length;

  const canGenerate = criteriaCount < 10;
  const remainingSlots = 10 - criteriaCount;

  // Initialize with AI criteria if available and no criteria selected
  useEffect(() => {
    if (aiGeneratedCriteria && aiGeneratedCriteria.length > 0 && selectedCriteria.length === 0) {
      const initialCriteria = aiGeneratedCriteria.slice(0, 10).map((criterion, index) => ({
        id: criterion.id || `ai_${index}`,
        name: criterion.name,
        description: criterion.description,
        category: criterion.category || 'IA Généré',
        isAIGenerated: true,
        isCompliant: true,
        validationStatus: 'validated' as const,
        order: index
      }));

      initialCriteria.forEach(criterion => append(criterion));
    }
  }, [aiGeneratedCriteria, selectedCriteria.length, append]);

  const handleGenerateAdditionalCriteria = async () => {
    if (!canGenerate) return;

    setIsGenerating(true);
    try {
      console.warn('🎯 Génération de critères - Données:', {
        existingCriteria: selectedCriteria.length,
        remainingSlots,
        campaignData: campaignData ? 'Présent' : 'Absent'
      });

      const { data, error } = await supabase.functions.invoke('generate-additional-criteria', {
        body: {
          existingCriteria: selectedCriteria,
          remainingCount: remainingSlots,
          campaignData: campaignData || {}
        }
      });

      if (error) {
        console.error('Erreur Supabase:', error);
        throw error;
      }

      console.warn('📥 Réponse reçue:', data);

      if (data && Array.isArray(data.criteria)) {
        data.criteria.forEach((criterion: any, index: number) => {
          append({
            id: `generated_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`,
            name: criterion.name,
            description: criterion.description,
            category: criterion.category || 'IA Généré',
            isAIGenerated: true,
            isNewlyGenerated: true, // Marquer comme nouveau critère
            isCompliant: true,
            validationStatus: 'validated' as const,
            order: selectedCriteria.length + index
          });
        });

        toast({
          title: "Critères générés",
          description: `${data.criteria.length} nouveaux critères ont été ajoutés.`,
        });

        // Sauvegarde automatique après génération de nouveaux critères
        if (onSaveDraft) {
          setTimeout(() => {
            onSaveDraft();
          }, 100);
        }
      } else {
        console.error('Réponse inattendue de la génération:', data);
        toast({
          title: "Format inattendu",
          description: (data && data.error) ? `IA: ${data.error}` : "Réponse IA invalide. Réessayez dans quelques secondes.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Erreur génération critères:', error);
      const message = error?.message || 'Impossible de générer de nouveaux critères. Veuillez réessayer.';
      toast({
        title: "Erreur",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpdateCriterion = (index: number, updatedCriterion: any) => {
    update(index, updatedCriterion);
    
    // Sauvegarde automatique après modification d'un critère
    if (onSaveDraft) {
      // Délai court pour permettre la mise à jour du formulaire
      setTimeout(() => {
        onSaveDraft();
      }, 100);
    }
  };

  const handleRemoveCriterion = (index: number) => {
    remove(index);
    
    // Sauvegarde automatique après suppression d'un critère
    if (onSaveDraft) {
      setTimeout(() => {
        onSaveDraft();
      }, 100);
    }
  };

  const handleRestoreAICriteria = () => {
    if (!aiGeneratedCriteria) return;

    try {
      // Clear all current criteria safely
      const currentLength = fields.length;
      for (let i = currentLength - 1; i >= 0; i--) {
        remove(i);
      }

      // Add original AI criteria
      const restoredCriteria = aiGeneratedCriteria.slice(0, 10).map((criterion, index) => ({
        id: criterion.id || `restored_${Date.now()}_${index}`,
        name: criterion.name,
        description: criterion.description,
        category: criterion.category || 'IA Généré',
        isAIGenerated: true,
        isCompliant: true,
        validationStatus: 'validated' as const,
        order: index
      }));

      // Add criteria one by one
      restoredCriteria.forEach(criterion => {
        append(criterion);
      });

      toast({
        title: "Critères restaurés",
        description: "Les critères générés par l'IA ont été restaurés.",
      });

      // Sauvegarde automatique après restauration des critères IA
      if (onSaveDraft) {
        setTimeout(() => {
          onSaveDraft();
        }, 100);
      }
    } catch (_error) {
      console.error('Erreur lors de la restauration:', _error);
      toast({
        title: "Erreur",
        description: "Impossible de restaurer les critères. Veuillez recharger la page.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-gray-800 text-lg font-medium font-bricolage leading-tight">
        Critères d'évaluation personnalisés
      </div>
      {/* En-tête avec indicateur IA et bouton restaurer */}
      {aiGeneratedCriteria && aiGeneratedCriteria.length > 0 && (
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.44 12.076C4.24539 12.0117 4.0441 11.9698 3.84 11.951L3.699 11.945C2.761 11.945 2 12.728 2 13.693C2 14.567 2.623 15.291 3.437 15.421C3.465 15.4343 3.51067 15.4633 3.574 15.508C3.844 15.703 4.434 16.245 5.197 17.619C5.495 18.157 6.048 18.492 6.65 18.499C7.05794 18.5037 7.45349 18.3589 7.762 18.092M15 5.5C13.65 6.015 12.378 6.989 11.277 8.03C10.893 8.393 10.517 8.776 10.154 9.169M21.897 6.63C22.217 7.528 21.767 8.143 20.899 8.748C20.197 9.236 19.304 9.765 18.357 10.67C17.429 11.557 16.523 12.625 15.718 13.676C14.7332 14.9488 13.8281 16.2814 13.008 17.666C12.8629 17.921 12.6524 18.1328 12.3982 18.2793C12.1441 18.4259 11.8554 18.5021 11.562 18.5C11.2683 18.4937 10.9816 18.4095 10.7311 18.2561C10.4805 18.1028 10.2752 17.8857 10.136 17.627C9.388 16.264 8.81 15.726 8.544 15.533C7.807 14.996 7 14.903 7 13.733C7 12.776 7.746 12 8.667 12C9.325 12.027 9.929 12.309 10.456 12.693C10.798 12.942 11.161 13.271 11.538 13.705C11.98 13.051 12.513 12.297 13.111 11.516C13.979 10.383 15.003 9.166 16.101 8.117C17.181 7.085 18.431 6.119 19.754 5.609C20.617 5.276 21.576 5.732 21.897 6.63Z" stroke="#32CD32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="text-gray-800 text-lg font-medium font-bricolage">
              Critères générés par l'IA disponibles
            </div>
          </div>
          <button 
            onClick={handleRestoreAICriteria}
            className="h-11 px-3 py-3.5 bg-teal-400 rounded-[10px] flex items-center gap-1 hover:bg-teal-500 transition-colors"
          >
            <div className="text-white text-sm font-semibold font-bricolage">
              Restaurer les critères de l'IA
            </div>
          </button>
        </div>
      )}

      {/* Bouton Générer nouveau critère */}
      {canGenerate && (
        <button 
          onClick={handleGenerateAdditionalCriteria}
          disabled={isGenerating}
          className="w-full h-11 pl-2.5 pr-3 py-3 rounded-[10px] border border-teal-400 flex justify-center items-center gap-1 text-teal-400 font-semibold font-bricolage text-sm hover:bg-teal-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4" />
          <div className="font-bricolage">
            {isGenerating ? 
              `Génération de ${remainingSlots} critère${remainingSlots !== 1 ? 's' : ''}...` : 
              `Générer ${remainingSlots} nouveau${remainingSlots !== 1 ? 'x' : ''} critère${remainingSlots !== 1 ? 's' : ''}`
            }
          </div>
        </button>
      )}

      {/* Liste des critères */}
      <div className="space-y-3">
        {selectedCriteria.map((criterion: any, index: number) => (
          <OptimizedEditableCriterionCard
            key={criterion.id || index}
            criterion={criterion}
            index={index}
            onUpdate={handleUpdateCriterion}
            onRemove={handleRemoveCriterion}
            onSaveDraft={onSaveDraft}
            canRemove={criteriaCount > 1}
          />
        ))}

        {selectedCriteria.length === 0 && (
          <div className="text-center py-8 text-neutral-400">
            <Sparkles className="h-12 w-12 mx-auto mb-4 text-neutral-400/50" />
            <p className="font-bricolage">Aucun critère configuré</p>
            <p className="text-sm font-bricolage">Utilisez le bouton de génération pour commencer</p>
          </div>
        )}
      </div>

      {/* Message limite de critères */}
      <div className="flex justify-end items-center gap-1.5">
        <div className="text-neutral-400 text-sm font-normal font-bricolage">
          Limite:
        </div>
        <div className="text-neutral-400 text-sm font-normal font-bricolage">
          {criteriaCount}/10 critères maximum
        </div>
      </div>

      {/* Message d'information pour le lancement */}
      {criteriaCount >= 5 && criteriaCount <= 10 && (
        <div className="mt-4 p-4 bg-green-50 rounded-[10px] border border-green-200">
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm font-bricolage">
              Vous avez configuré {criteriaCount} critères. Vous pouvez maintenant lancer votre campagne.
            </span>
          </div>
        </div>
      )}

      {criteriaCount < 5 && (
        <Alert className="mt-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="font-bricolage">
            Vous devez configurer au moins 5 critères d'évaluation pour pouvoir lancer votre campagne.
            Il vous manque encore {5 - criteriaCount} critère{5 - criteriaCount !== 1 ? 's' : ''}.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};